/**
 * The record of margins a built case had to move (`marginClamps` on a mounted
 * Anbauteil), and the reads on it that need no geometry. The build that writes
 * it lives in `@project/manufacturer` (`settleMargin`); everything that turns
 * instance data into a Vorlage reads it from here.
 */

/**
 * A margin the case could not honour: `requested` is the value that was asked
 * for, `applied` the one the case settled at instead, and `alignment` the
 * anchor the margin was measured from at the time. A margin means a different
 * position under another anchor, so the request only counts while the option
 * still has that alignment.
 */
export interface MarginClamp {
  requested: number;
  applied: number;
  alignment?: string;
}

/** Clamped margins of one option, keyed by margin field. */
export interface MarginClamps {
  horizontalMargin?: MarginClamp;
  verticalMargin?: MarginClamp;
  margin?: MarginClamp;
}

/** Margin fields the case clamps into the room its geometry leaves. */
export type ClampableMarginField = keyof MarginClamps;

export const CLAMPABLE_MARGIN_FIELDS: readonly ClampableMarginField[] = [
  "horizontalMargin",
  "verticalMargin",
  "margin",
];

/** The field holding the anchor each margin is measured from. */
const MARGIN_ALIGNMENT_FIELDS: Record<ClampableMarginField, string> = {
  horizontalMargin: "horizontalAlignment",
  verticalMargin: "verticalAlignment",
  margin: "alignment",
};

/** The anchor `option`'s `field` is measured from, if it has one. */
export function marginAlignment(
  option: object,
  field: ClampableMarginField,
): string | undefined {
  const alignment = (option as Record<string, unknown>)[
    MARGIN_ALIGNMENT_FIELDS[field]
  ];
  return typeof alignment === "string" ? alignment : undefined;
}

function isMarginClamp(value: unknown): value is MarginClamp {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const { requested, applied, alignment } = value as Record<string, unknown>;
  return (
    Number.isFinite(requested) &&
    Number.isFinite(applied) &&
    (alignment === undefined || typeof alignment === "string")
  );
}

/** A clamp entry, without an `alignment` key when there is no anchor. */
export function createMarginClamp(
  requested: number,
  applied: number,
  alignment: string | undefined,
): MarginClamp {
  return alignment === undefined
    ? { requested, applied }
    : { requested, applied, alignment };
}

/**
 * The well-formed entries of `option.marginClamps`. The record arrives with
 * saved positions and host-supplied configurations, so anything that is not a
 * pair of finite numbers on a known margin field is ignored rather than trusted.
 */
export function readMarginClamps(option: object): MarginClamps {
  const { marginClamps } = option as { marginClamps?: unknown };
  const clamps: MarginClamps = {};
  if (typeof marginClamps !== "object" || marginClamps === null) {
    return clamps;
  }
  for (const field of CLAMPABLE_MARGIN_FIELDS) {
    const clamp = (marginClamps as Record<string, unknown>)[field];
    if (isMarginClamp(clamp)) {
      clamps[field] = createMarginClamp(
        clamp.requested,
        clamp.applied,
        clamp.alignment,
      );
    }
  }
  return clamps;
}

/**
 * The clamps behind `option`'s current margins. A clamp whose field no longer
 * holds its `applied` value, or whose anchor changed since, is stale (edited,
 * not yet rebuilt) and left out.
 */
export function activeMarginClamps(option: object): MarginClamps {
  const values = option as Partial<Record<ClampableMarginField, unknown>>;
  const active: MarginClamps = {};
  for (const [field, clamp] of Object.entries(readMarginClamps(option)) as [
    ClampableMarginField,
    MarginClamp,
  ][]) {
    if (
      values[field] === clamp.applied &&
      clamp.alignment === marginAlignment(option, field)
    ) {
      active[field] = clamp;
    }
  }
  return active;
}

/**
 * `option` as it was asked for: every margin the case is holding away from its
 * request put back to that request, and the record dropped. Needs no build, so
 * it answers "is this still the config that was applied?" even before the case
 * has computed its margin ranges.
 */
export function withRequestedMargins<T extends object>(option: T): T {
  const active = activeMarginClamps(option);
  const { marginClamps: _clamps, ...rest } = option as T & {
    marginClamps?: MarginClamps;
  };
  for (const [field, clamp] of Object.entries(active) as [
    ClampableMarginField,
    MarginClamp,
  ][]) {
    (rest as Record<string, unknown>)[field] = clamp.requested;
  }
  return rest as T;
}

/**
 * `option` with the clamp on `field` forgotten, keeping the margin where the
 * case put it. How a user accepts the clamped value: without it, a margin left
 * at `applied` still counts as untouched and returns to its request once the
 * case has room.
 */
export function withoutMarginClamp<T extends object>(
  option: T,
  field: ClampableMarginField,
): T {
  const clamps = readMarginClamps(option);
  delete clamps[field];
  const { marginClamps: _clamps, ...rest } = option as T & {
    marginClamps?: MarginClamps;
  };
  return (
    Object.keys(clamps).length > 0 ? { ...rest, marginClamps: clamps } : rest
  ) as T;
}
